import * as React from "react";
import { Select, IOption } from "../../atoms/forms/Select";
import { ISelfManagedSelectControls } from "./ICommonInputsControls";
import { useTranslation } from 'react-i18next';

export type DisabilityStatus = "None" | "Suspected" | "Confirmed";

interface IDisabilityStatusOption extends IOption {
    status: DisabilityStatus;
}



// Props interface
interface IProps {
}

// Actual Component
export type AllProps = IProps & ISelfManagedSelectControls<DisabilityStatus>;
export const DisabilityStatusSelect = (props: AllProps) => {
    const { t } = useTranslation('client');

    const disabilityStatusOptions: IDisabilityStatusOption[] = [
        { value: "None", label: t('disabilityStatusNone', ), status: "None" },
        { value: "Suspected", label: t('disabilityStatusSuspected', ), status: "Suspected" },
        { value: "Confirmed", label: t('disabilityStatusConfirmed', ), status: "Confirmed" }
    ];
    
    // Internal state for selected status
    const [selectedStatus, setSelectedStatus] = React.useState<IDisabilityStatusOption | null>(() => {
        if (props.value) {
            return disabilityStatusOptions.find(s => s.status === props.value) || null;
        }
        return null;
    });

    // Update selected status when value prop changes
    React.useEffect(() => {
        if (props.value) {
            const status = disabilityStatusOptions.find(s => s.status === props.value);
            setSelectedStatus(status || null);
        } else {
            setSelectedStatus(null);
        }
    }, [props.value]);

    // Translate labels
    const translatedOptions = React.useMemo(() => {
        return disabilityStatusOptions.map(option => ({
            ...option,
            label: t(`disabilityStatus.${option.status}`, option.status)
        }));
    }, [t]);

    // Handle change
    const handleChange = (newValue: IDisabilityStatusOption | null, isValid: boolean) => {
        setSelectedStatus(newValue);
        if (props.onChange) {
            props.onChange(newValue?.status || null, isValid);
        }
    };

    // Wrap onValidate to work with IDisabilityStatusOption
    const handleValidate = props.onValidate ? (option: IDisabilityStatusOption | null): [boolean, string] => {
        return props.onValidate!(option?.status || null);
    } : undefined;

    return (
        <Select
            label={props.label || t('disabilityStatus', 'Beperking status')}
            value={selectedStatus}
            options={disabilityStatusOptions}
            loading={props.loading}
            disabled={props.disabled}
            required={props.required}
            error={props.error}
            placeholder={props.placeholder}
            prevalidate={props.prevalidate}
            icon={props.icon}
            onChange={handleChange}
            onValidate={handleValidate}
        />
    );
}

